import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type Language = "el" | "en";

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations = {
  el: {
    // Navigation
    home: "Αρχική",
    programs: "Προγράμματα",
    nutrition: "Διατροφή",
    signup: "Εγγραφή",
    
    // Hero Section
    hero_title: "Χτίσε Δύναμη.",
    hero_subtitle: "Αλλαξε Σώμα.",
    hero_description: "Ολοκληρωμένα προγράμματα προπόνησης σε PDF — δύναμη, υπερτροφία, κινητικότητα — με οδηγίες βήμα-βήμα και υποστήριξη από coach.",
    hero_cta1: "Δες Προγράμματα",
    hero_cta2: "Κλείσε 1:1 Session",
    hero_bullet1: "Δομημένα προγράμματα 6–12 εβδομάδων με progressive overload",
    hero_bullet2: "Αναλυτικές οδηγίες τεχνικής για κάθε άσκηση",
    hero_bullet3: "Προσωπική υποστήριξη μέσω 1:1 coaching sessions",
    hero_trust1: "Ψηφιακή παράδοση άμεσα",
    hero_trust2: "Ασφαλείς πληρωμές",
    hero_trust3: "Υποστήριξη από coach",
    
    // About Section
    about_title: "Γεια, είμαι η Stefania!",
    about_p1: "Ως πιστοποιημένη προπονήτρια δύναμης και διατροφής, έχω βοηθήσει εκατοντάδες ανθρώπους να ανακαλύψουν τη δύναμή τους - όχι μόνο στο γυμναστήριο, αλλά και στη ζωή τους.",
    about_p2: "Η φιλοσοφία μου είναι απλή: η δύναμη χτίζεται με συνέπεια, υποστήριξη και τη σωστή καθοδήγηση. Δεν πρόκειται για ακραίες δίαιτες ή απρόσιτους στόχους - πρόκειται για βιώσιμη μεταμόρφωση που διαρκεί.",
    about_p3: "Ό,τι κι αν είναι ο στόχος σου - να χάσεις λίπος, να χτίσεις μυς, να γίνεις πιο δυνατός/ή - είμαι εδώ για να σε υποστηρίξω σε κάθε βήμα.",
    
    // Featured Products
    featured_title: "Δημοφιλή Προγράμματα",
    featured_description: "Επέλεξε το πρόγραμμα που ταιριάζει στους στόχους σου και ξεκίνα τη μεταμόρφωσή σου σήμερα.",
    view_all_programs: "Δες Όλα τα Προγράμματα",
    
    // How It Works
    how_it_works: "Πώς Λειτουργεί",
    how_it_works_subtitle: "Από την επιλογή μέχρι την πρώτη προπόνηση — σε 4 απλά βήματα.",
    step1_title: "Διαλέγεις πρόγραμμα",
    step1_description: "Περιηγήσου στα προγράμματα και βρες αυτό που ταιριάζει στο επίπεδο και τους στόχους σου.",
    step2_title: "Ολοκληρώνεις πληρωμή",
    step2_description: "Ασφαλής πληρωμή με κάρτα μέσω Stripe. Δεν αποθηκεύουμε στοιχεία κάρτας.",
    step3_title: "Παίρνεις άμεσα πρόσβαση",
    step3_description: "Μετά την πληρωμή, λαμβάνεις email επιβεβαίωσης και κατεβάζεις το PDF αμέσως.",
    step4_title: "Ξεκινάς προπόνηση + υποστήριξη",
    step4_description: "Ακολούθησε το πρόγραμμα βήμα-βήμα. Αν χρειαστείς βοήθεια, στείλε email ή κλείσε 1:1 session.",
    how_it_works_time: "Χρόνος ανά εβδομάδα: 3–5 προπονήσεις × 45–75 λεπτά, ανάλογα με το πρόγραμμα.",
    how_it_works_contact: "Έχεις ερωτήσεις;",
    how_it_works_contact_link: "Στείλε μας μήνυμα",
    
    // Social Proof
    social_proof_title: "Αποτελέσματα από Πραγματικούς Ανθρώπους",
    social_proof_subtitle: "Δες τι λένε όσοι ακολούθησαν τα προγράμματά μας.",
    social_proof_disclaimer: "Τα αποτελέσματα διαφέρουν ανά άτομο.",
    social_proof_cta: "Δες το πρόγραμμα που μου ταιριάζει",
    social_proof_early_badge: "Νέα Πλατφόρμα",
    social_proof_early_title: "Γίνε από τους πρώτους που θα δοκιμάσουν τα προγράμματά μου",
    social_proof_early_description: "Τα προγράμματα μόλις κυκλοφόρησαν. Ξεκίνα τώρα και μοιράσου την εμπειρία σου — η γνώμη σου μετράει.",
    social_proof_early_cta: "Δες τα Προγράμματα",
    
    // CTA
    cta_title: "Έτοιμος/η να Ξεκινήσεις;",
    cta_description: "Μην περιμένεις άλλο. Το ταξίδι προς την καλύτερη έκδοση του εαυτού σου ξεκινά σήμερα.",
    view_programs: "Δες τα Προγράμματα",
    
    // Reviews Section
    loading_reviews: "Φόρτωση αξιολογήσεων...",
    no_reviews: "Δεν υπάρχουν ακόμα κριτικές.",
    submit_review: "Υποβολή Κριτικής",
    your_rating: "Η Αξιολόγησή σου",
    your_comment: "Το Σχόλιό σου",
    submit: "Υποβολή",
    submitting: "Υποβολή...",
    review_success: "Επιτυχία!",
    review_success_desc: "Η κριτική σας δημοσιεύτηκε!",
    review_error: "Σφάλμα",
    review_error_desc: "Κάτι πήγε στραβά. Παρακαλώ δοκίμασε ξανά.",
    
    // Footer
    footer_description: "Εξειδικευμένα προγράμματα προπόνησης και διατροφής για πραγματική μεταμόρφωση.",
    footer_by: "Από τη Stefania Meraklis",
    quick_links: "Γρήγοροι Σύνδεσμοι",
    about: "Σχετικά",
    contact: "Επικοινωνία",
    all_rights: "Όλα τα δικαιώματα διατηρούνται.",
    privacy_policy: "Πολιτική Απορρήτου",
    terms_of_service: "Όροι Χρήσης",
    
    // Programs Page
    programs_hero_title: "Τα Προγράμματά μου",
    programs_hero_subtitle: "Επαγγελματικά προγράμματα δύναμης και μεταμόρφωσης σχεδιασμένα για πραγματικά αποτελέσματα.",
    faq1_question: "Για ποιον είναι αυτά τα προγράμματα;",
    faq1_answer: "Τα προγράμματα είναι για όλους - από αρχάριους έως προχωρημένους αθλητές. Κάθε πρόγραμμα περιλαμβάνει σαφείς οδηγίες και μπορεί να προσαρμοστεί στο επίπεδό σου.",
    faq2_question: "Χρειάζομαι εξοπλισμό γυμναστηρίου;",
    faq2_answer: "Τα περισσότερα προγράμματα απαιτούν βασικό εξοπλισμό γυμναστηρίου (μπάρα, βάρη, καθιστικό). Αν έχεις ερωτήσεις για συγκεκριμένο εξοπλισμό, μη διστάσεις να ρωτήσεις!",
    faq3_question: "Πόσο καιρό διαρκεί κάθε πρόγραμμα;",
    faq3_answer: "Η διάρκεια κυμαίνεται από 4 έως 12 εβδομάδες ανάλογα με το πρόγραμμα. Κάθε προϊόν έχει λεπτομερείς πληροφορίες για τη διάρκεια και το περιεχόμενο.",
    faq4_question: "Μπορώ να πάρω προσωπική καθοδήγηση;",
    faq4_answer: "Ναι! Προσφέρω 1:1 online coaching sessions για εξατομικευμένη καθοδήγηση. Δες τα διαθέσιμα προγράμματα ή επικοινώνησε μαζί μου για περισσότερες πληροφορίες.",
    faq5_question: "Τι περιλαμβάνει το κόστος;",
    faq5_answer: "Το κόστος περιλαμβάνει πρόσβαση στο πλήρες πρόγραμμα σε μορφή PDF με σαφείς οδηγίες, προγράμματα προπονήσεων και όλα τα απαραίτητα υλικά για να ξεκινήσεις άμεσα.",
    
    // Meal Plans Page
    meal_plans_title: "Διατροφικά Προγράμματα",
    meal_plans_subtitle: "Εξατομικευμένα διαιτολόγια θα είναι σύντομα διαθέσιμα. Μείνε συντονισμένος/η!",
    coming_soon: "Έρχεται Σύντομα",
    coming_soon_description: "Εξατομικευμένα διαιτολόγια θα είναι σύντομα διαθέσιμα. Μείνε συντονισμένος/η!",
    find_ideal_plan: "Βρες το Ιδανικό Σου Πλάνο",
    find_ideal_plan_description: "Απάντησε σε λίγες ερωτήσεις για να βρούμε το κατάλληλο πρόγραμμα διατροφής για εσένα.",
    body_weight: "Σωματικό Βάρος",
    under_60kg: "Κάτω από 60kg",
    weight_60_80kg: "60-80kg",
    over_80kg: "Πάνω από 80kg",
    activity_level: "Επίπεδο Δραστηριότητας",
    select_activity: "Επίλεξε επίπεδο δραστηριότητας",
    sedentary: "Καθιστική ζωή (λίγη ή καθόλου άσκηση)",
    moderate: "Μέτρια δραστηριότητα (άσκηση 3-5 φορές την εβδομάδα)",
    high: "Υψηλή δραστηριότητα (άσκηση 6-7 φορές την εβδομάδα)",
    recommended_plan: "Συνιστώμενο Πρόγραμμα",
    recommended_plan_description: "Βασισμένο στα στοιχεία σου, αυτό το πλάνο θα σε βοηθήσει να πετύχεις τους στόχους σου.",
    daily_calories: "Εξατομικευμένη πρόσληψη θερμίδων",
    recipes_tips: "100+ συνταγές και συμβουλές μαγειρικής",
    grocery_lists: "Εβδομαδιαίες λίστες αγορών",
    meal_alternatives: "Εναλλακτικές επιλογές γεύματος",
    recommended_price: "Προτεινόμενη Τιμή",
    buy_now_btn: "Αγόρασε Τώρα",
    looking_for_package: "Ψάχνεις για Ολοκληρωμένο Πακέτο;",
    transformation_description: "Συνδύασε προπόνηση και διατροφή για μέγιστα αποτελέσματα με το Transformation Program μου.",
    weeks_training: "12 εβδομάδες δομημένης προπόνησης",
    custom_meal_plan: "Εξατομικευμένο πρόγραμμα διατροφής",
    monthly_checkins: "Μηνιαίοι έλεγχοι προόδου",
    email_support: "Υποστήριξη μέσω email",
    learn_more: "Μάθε Περισσότερα",
    
    // Product Detail
    back_to_programs: "Επιστροφή στα Προγράμματα",
    back_to_programs_link: "Επιστροφή στα Προγράμματα",
    breadcrumb_home: "Αρχική",
    breadcrumb_programs: "Προγράμματα",
    whats_included: "Τι Περιλαμβάνεται",
    duration: "Διάρκεια",
    duration_label: "Διάρκεια",
    level: "Επίπεδο",
    level_label: "Επίπεδο",
    format: "Μορφή",
    format_label: "Μορφή",
    equipment_label: "Εξοπλισμός",
    workout_time_label: "Χρόνος Προπόνησης",
    days_per_week_label: "Ημέρες / Εβδομάδα",
    delivery_label: "Παράδοση",
    support_label: "Υποστήριξη",
    buy_program_cta: "Αγόρασε το Πρόγραμμα",
    buy_now_price: "Αγόρασε Τώρα",
    secure_payment: "Ασφαλής πληρωμή μέσω Stripe · Ψηφιακή παράδοση άμεσα",
    about_program: "Σχετικά με το Πρόγραμμα",
    who_is_this_for: "Για Ποιον Είναι",
    program_specs: "Χαρακτηριστικά Προγράμματος",
    faq_title: "Συχνές Ερωτήσεις",
    faq_title_detail: "Ερωτήσεις για αυτό το Πρόγραμμα",
    refund_note: "Λόγω ψηφιακής φύσης, δεν γίνονται επιστροφές.",
    refund_link: "Δες την πολιτική επιστροφών",
    product_not_found: "Το προϊόν δεν βρέθηκε",
    anonymous: "Ανώνυμος",
    no_reviews_yet: "Δεν υπάρχουν κριτικές ακόμα",
    leave_review: "Αφήστε μια Κριτική",
    sign_in_to_review: "Συνδεθείτε για να αφήσετε μια κριτική",
    rating: "Αξιολόγηση",
    your_review: "Η Κριτική σας",
    share_experience: "Μοιραστείτε την εμπειρία σας...",
    
    // Header
    my_purchases: "Οι Αγορές μου",
    admin: "Διαχειριστής",
    admin_dashboard: "Πίνακας Διαχειριστή",
    settings: "Ρυθμίσεις",
    logout: "Αποσύνδεση",
    
    // Auth Pages
    join_morefitlyfe: "Γίνε Μέλος του MoreFitLyfe",
    start_transformation: "Ξεκίνα το ταξίδι της μεταμόρφωσής σου σήμερα",
    sign_up: "Εγγραφή",
    log_in: "Σύνδεση",
    full_name: "Ονοματεπώνυμο",
    email: "Email",
    password: "Κωδικός",
    i_agree_to: "Συμφωνώ με την",
    and: "και",
    create_account: "Δημιουργία Λογαριασμού",
    creating_account: "Δημιουργία...",
    logging_in: "Σύνδεση...",
    back_to_home: "Επιστροφή στην Αρχική",
    loading: "Φόρτωση...",
    
    // Forgot/Reset Password
    forgot_password: "Ξεχάσατε τον κωδικό;",
    forgot_password_desc: "Εισάγετε το email σας και θα σας στείλουμε έναν σύνδεσμο για επαναφορά.",
    send_reset_link: "Αποστολή συνδέσμου",
    sending: "Αποστολή...",
    password_reset_sent: "Email απεστάλη",
    password_reset_sent_desc: "Ελέγξτε τα εισερχόμενά σας για τον σύνδεσμο επαναφοράς.",
    check_your_email: "Ελέγξτε το email σας",
    password_reset_instructions: "Σας έχουμε στείλει έναν σύνδεσμο επαναφοράς κωδικού. Ελέγξτε τα εισερχόμενα και τα ανεπιθύμητα.",
    try_another_email: "Δοκιμάστε άλλο email",
    back_to_login: "Επιστροφή στη σύνδεση",
    reset_password: "Επαναφορά κωδικού",
    reset_password_desc: "Εισάγετε τον νέο σας κωδικό.",
    new_password: "Νέος κωδικός",
    confirm_password: "Επιβεβαίωση κωδικού",
    update_password: "Ενημέρωση κωδικού",
    updating: "Ενημέρωση...",
    password_updated: "Κωδικός ενημερώθηκε",
    password_updated_desc: "Ο κωδικός σας ενημερώθηκε επιτυχώς.",
    password_reset_success: "Επιτυχής επαναφορά",
    password_reset_success_desc: "Ο κωδικός σας επαναφέρθηκε επιτυχώς.",
    invalid_reset_link: "Ο σύνδεσμος επαναφοράς δεν είναι έγκυρος ή έχει λήξει.",
    request_new_link: "Ζητήστε νέο σύνδεσμο",
    redirecting_home: "Θα ανακατευθυνθείτε στην αρχική σελίδα...",
    go_to_home: "Μετάβαση στην αρχική",
    error: "Σφάλμα",
    
    // Email Verification
    verify_email_title: "Επαλήθευση Email",
    verify_email_sent: "Ελέγξτε το email σας",
    verify_email_sent_desc: "Σας στείλαμε ένα email επαλήθευσης. Κάντε κλικ στον σύνδεσμο για να ενεργοποιήσετε τον λογαριασμό σας.",
    resend_verification: "Επαναποστολή email επαλήθευσης",
    resending: "Επαναποστολή...",
    verification_sent: "Email απεστάλη",
    verification_sent_desc: "Ελέγξτε τα εισερχόμενα και τα ανεπιθύμητα.",
    verification_success_title: "Email Επαληθεύτηκε!",
    verification_success_desc: "Ο λογαριασμός σας είναι τώρα ενεργός. Μπορείτε να συνδεθείτε.",
    redirecting_to_login: "Ανακατεύθυνση στη σύνδεση",
    go_to_login: "Μετάβαση στη σύνδεση",
    email_not_verified: "Το email σας δεν έχει επαληθευτεί",
    email_not_verified_desc: "Παρακαλώ ελέγξτε τα εισερχόμενά σας και κάντε κλικ στον σύνδεσμο επαλήθευσης.",
    
    // Toast Messages
    toast_sign_in_required: "Παρακαλώ συνδεθείτε για να αφήσετε κριτική",
    toast_review_submitted: "Η κριτική σας δημοσιεύτηκε!",
    toast_review_failed: "Αποτυχία υποβολής κριτικής",
    toast_review_blocked: "Παρακαλώ αναθεωρήστε την κριτική σας. Κάποιες λέξεις δεν είναι κατάλληλες για την κοινότητά μας.",
    toast_review_blocked_spam: "Παρακαλώ γράψτε μια πιο φυσική κριτική χωρίς υπερβολικά κεφαλαία ή επαναλαμβανόμενους χαρακτήρες.",
    toast_settings_saved: "Οι ρυθμίσεις αποθηκεύτηκαν",
    toast_settings_saved_desc: "Οι προτιμήσεις σας ενημερώθηκαν επιτυχώς",
    toast_language_changed: "Η γλώσσα άλλαξε",
    toast_language_greek: "Ελληνικά ενεργοποιήθηκαν",
    toast_language_english: "English activated",
    toast_navigating_home: "Μετάβαση στην αρχική σελίδα",
    toast_navigating_programs: "Μετάβαση στα προγράμματα",
    toast_navigating_meals: "Μετάβαση στη διατροφή",
    
    // Settings Page
    settings_title: "Ρυθμίσεις",
    settings_description: "Διαχειριστείτε τις προτιμήσεις και τις ρυθμίσεις του λογαριασμού σας",
    keyboard_shortcuts: "Συντομεύσεις πληκτρολογίου",
    keyboard_shortcuts_desc: "Ενεργοποιήστε ή απενεργοποιήστε συντομεύσεις πληκτρολογίου για γρήγορη πλοήγηση",
    keyboard_shortcuts_help: "Πατήστε ? για να δείτε όλες τις διαθέσιμες συντομεύσεις",
    language_preferences: "Προτιμήσεις γλώσσας",
    language_preferences_desc: "Επιλέξτε την προτιμώμενη γλώσσα για τη διεπαφή",
    default_language: "Προεπιλεγμένη γλώσσα",
    greek: "Ελληνικά",
    english: "English",
    change: "Αλλαγή",
    notification_preferences: "Προτιμήσεις ειδοποιήσεων",
    notification_preferences_desc: "Διαχειριστείτε πώς και πότε θέλετε να λαμβάνετε ειδοποιήσεις",
    email_notifications: "Ειδοποιήσεις email",
    email_notifications_desc: "Λήψη ειδοποιήσεων μέσω email",
    push_notifications: "Push ειδοποιήσεις",
    push_notifications_desc: "Λήψη push ειδοποιήσεων στη συσκευή σας",
    marketing_emails: "Marketing emails",
    marketing_emails_desc: "Λήψη προσφορών και ενημερώσεων προϊόντων",
    system_updates: "Ενημερώσεις συστήματος",
    system_updates_desc: "Σημαντικές ειδοποιήσεις για το λογαριασμό σας",
    audit_sounds: "Ήχοι ελέγχου διαχειριστή",
    audit_sounds_desc: "Αναπαραγωγή ήχου για κρίσιμες ενέργειες διαχειριστή (διαγραφές, επαναφορές)",
    preview_sounds: "Προεπισκόπηση ήχων",
    sound_default: "Προεπιλογή",
    sound_warning: "Προειδοποίηση",
    sound_destructive: "Κρίσιμο",
    save_settings: "Αποθήκευση ρυθμίσεων",
    
    // Two-Factor Authentication
    "2fa_title": "Αυθεντικοποίηση Δύο Παραγόντων",
    "2fa_description": "Προσθέστε ένα επιπλέον επίπεδο ασφαλείας στον λογαριασμό σας χρησιμοποιώντας μια εφαρμογή αυθεντικοποίησης.",
    "2fa_enabled": "Ενεργοποιημένη",
    "2fa_disabled": "Απενεργοποιημένη",
    "2fa_enable": "Ενεργοποίηση 2FA",
    "2fa_disable": "Απενεργοποίηση 2FA",
    "2fa_scan_qr": "Σαρώστε αυτόν τον κωδικό QR με την εφαρμογή αυθεντικοποίησής σας (π.χ. Google Authenticator, Authy):",
    "2fa_manual_entry": "Ή εισάγετε αυτόν τον κωδικό χειροκίνητα:",
    "2fa_enter_code": "Εισάγετε τον εξαψήφιο κωδικό",
    "2fa_verify": "Επαλήθευση",
    "2fa_verify_enable": "Επαλήθευση & Ενεργοποίηση",
    "2fa_verifying": "Επαλήθευση...",
    "2fa_setting_up": "Ρύθμιση...",
    "2fa_disabling": "Απενεργοποίηση...",
    "2fa_active_message": "Η αυθεντικοποίηση δύο παραγόντων είναι ενεργή στον λογαριασμό σας.",
    "2fa_not_enabled_message": "Προστατέψτε τον λογαριασμό σας με αυθεντικοποίηση δύο παραγόντων. Θα χρειαστείτε μια εφαρμογή αυθεντικοποίησης στο τηλέφωνό σας.",
    "2fa_disable_confirm_title": "Απενεργοποίηση 2FA;",
    "2fa_disable_confirm_desc": "Αυτό θα αφαιρέσει το επιπλέον επίπεδο ασφαλείας από τον λογαριασμό σας. Θα χρειαστεί να το ρυθμίσετε ξανά αν θέλετε να το ενεργοποιήσετε αργότερα.",
    "2fa_verification_title": "Επαλήθευση Ταυτότητας",
    "2fa_verification_description": "Εισάγετε τον κωδικό από την εφαρμογή αυθεντικοποίησής σας για να συνεχίσετε.",
    "2fa_secret_copied": "Αντιγράφηκε!",
    "2fa_secret_copied_desc": "Ο μυστικός κωδικός αντιγράφηκε στο πρόχειρο.",
    
    // Keyboard Shortcuts Modal
    shortcuts_title: "Συντομεύσεις Πληκτρολογίου",
    shortcuts_description: "Χρησιμοποιήστε αυτές τις συντομεύσεις για γρήγορη πλοήγηση",
    shortcut_home: "Αρχική σελίδα",
    shortcut_programs: "Προγράμματα",
    shortcut_meals: "Διατροφή",
    shortcut_toggle_language: "Εναλλαγή γλώσσας",
    shortcut_show_shortcuts: "Εμφάνιση συντομεύσεων",

    // Admin Dashboard
    admin_dashboard_title: "Πίνακας Διαχειριστή",
    admin_dashboard_subtitle: "Διαχειριστείτε τις επιχειρηματικές σας λειτουργίες και παρακολουθήστε την απόδοση",
    admin_overview: "Επισκόπηση",
    admin_products: "Προϊόντα",
    admin_purchases: "Αγορές",
    admin_files: "Αρχεία",
    admin_reviews: "Κριτικές",
    admin_users: "Χρήστες",
    admin_revenue: "Έσοδα",
    admin_downloads: "Λήψεις",
    admin_limits: "Όρια",
    admin_audit: "Έλεγχος",
    total_revenue: "Συνολικά Έσοδα",
    total_purchases: "Συνολικές Αγορές",
    pending_reviews_count: "Εκκρεμείς Κριτικές",
    total_users: "Συνολικοί Χρήστες",
    view_in_revenue_tab: "Δείτε στην καρτέλα Έσοδα",
    view_in_purchases_tab: "Δείτε στην καρτέλα Αγορές",
    view_in_reviews_tab: "Δείτε στην καρτέλα Κριτικές",
    view_in_users_tab: "Δείτε στην καρτέλα Χρήστες",
    analytics_overview: "Επισκόπηση Αναλυτικών",
    analytics_overview_desc: "Δείτε λεπτομερή αναλυτικά στις παραπάνω καρτέλες",

    // Product Management
    product_management: "Διαχείριση Προϊόντων",
    product_management_desc: "Διαχειριστείτε τα προγράμματα προπόνησης και τα προϊόντα σας",
    loading_products: "Φόρτωση προϊόντων...",
    edit_product: "Επεξεργασία Προϊόντος",
    edit_product_desc: "Ενημερώστε τις λεπτομέρειες και την τιμολόγηση του προϊόντος",
    name_english: "Όνομα (Αγγλικά)",
    name_greek: "Όνομα (Ελληνικά)",
    description_english: "Περιγραφή (Αγγλικά)",
    description_greek: "Περιγραφή (Ελληνικά)",
    price_usd: "Τιμή ($)",
    image_url: "URL Εικόνας",
    cancel: "Ακύρωση",
    save_changes: "Αποθήκευση Αλλαγών",
    saving: "Αποθήκευση...",
    active: "Ενεργό",
    product_updated: "Το προϊόν ενημερώθηκε με επιτυχία",
    product_update_failed: "Αποτυχία ενημέρωσης προϊόντος",
    product_status_updated: "Η κατάσταση του προϊόντος ενημερώθηκε",

    // Purchase List
    purchase_history: "Ιστορικό Αγορών",
    purchase_history_desc: "Προβολή όλων των αγορών πελατών και των λεπτομερειών συναλλαγών",
    loading_purchases: "Φόρτωση αγορών...",
    no_purchases_yet: "Δεν υπάρχουν ακόμα αγορές",
    date: "Ημερομηνία",
    customer: "Πελάτης",
    product: "Προϊόν",
    amount: "Ποσό",
    status: "Κατάσταση",

    // User Management
    user_management: "Διαχείριση Χρηστών",
    user_management_desc: "Προβολή και διαχείριση εγγεγραμμένων χρηστών",
    loading_users: "Φόρτωση χρηστών...",
    no_users_yet: "Δεν υπάρχουν ακόμα χρήστες",
    name: "Όνομα",
    role: "Ρόλος",
    downloads: "Λήψεις",
    last_download: "Τελευταία Λήψη",
    registered: "Εγγραφή",
    actions: "Ενέργειες",
    never: "Ποτέ",
    reset_download_limit: "Επαναφορά Ορίου Λήψεων",
    reset_limit_confirm_title: "Επαναφορά Ορίου Λήψεων;",
    reset_limit_confirm_desc: "Αυτό θα διαγράψει όλο το ιστορικό λήψεων για τον/την {user} και θα του/της επιτρέψει να κατεβάσει αρχεία ξανά. Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.",
    reset_limit: "Επαναφορά Ορίου",
    download_limit_reset_success: "Το όριο λήψεων επαναφέρθηκε με επιτυχία",
    download_limit_reset_failed: "Αποτυχία επαναφοράς ορίου λήψεων",
    resend_welcome_email: "Επαναποστολή Email Καλωσορίσματος",
    resend_welcome_email_confirm_title: "Επαναποστολή Email Καλωσορίσματος;",
    resend_welcome_email_confirm_desc: "Αυτό θα στείλει ένα νέο email καλωσορίσματος στον/στην {user}.",
    resend_welcome_email_success: "Το email καλωσορίσματος στάλθηκε με επιτυχία",
    resend_welcome_email_failed: "Αποτυχία αποστολής email καλωσορίσματος",
    welcome_email_sent: "Στάλθηκε",
    
    // Email Template Editor
    email_template_editor: "Επεξεργαστής Προτύπου Email",
    email_template_editor_desc: "Προσαρμόστε το περιεχόμενο και την εμφάνιση των email καλωσορίσματος",
    email_template_load_error: "Αποτυχία φόρτωσης προτύπου email",
    email_template_saved: "Το πρότυπο email αποθηκεύτηκε με επιτυχία",
    email_template_save_error: "Αποτυχία αποθήκευσης προτύπου email",
    email_template_reset: "Το πρότυπο επαναφέρθηκε στις προεπιλογές",
    email_template_not_found: "Δεν βρέθηκε πρότυπο email",
    reset_to_default: "Επαναφορά σε Προεπιλογή",
    save_template: "Αποθήκευση Προτύπου",
    content: "Περιεχόμενο",
    branding: "Εμφάνιση",
    preview: "Προεπισκόπηση",
    email_subject: "Θέμα Email",
    email_heading: "Επικεφαλίδα",
    greeting_prefix: "Πρόθεμα Χαιρετισμού",
    intro_text: "Εισαγωγικό Κείμενο",
    body_text: "Κύριο Κείμενο",
    cta_button_text: "Κείμενο Κουμπιού",
    features_heading: "Επικεφαλίδα Χαρακτηριστικών",
    feature_list: "Λίστα Χαρακτηριστικών",
    feature_1_placeholder: "Χαρακτηριστικό 1",
    feature_2_placeholder: "Χαρακτηριστικό 2",
    feature_3_placeholder: "Χαρακτηριστικό 3",
    footer_text: "Κείμενο Υποσέλιδου",
    signature: "Υπογραφή",
    primary_color: "Κύριο Χρώμα",
    primary_color_desc: "Χρώμα κουμπιών και επικεφαλίδων",
    background_color: "Χρώμα Φόντου",
    background_color_desc: "Χρώμα φόντου email",
    text_color: "Χρώμα Κειμένου",
    text_color_desc: "Χρώμα κύριου κειμένου",
    admin_emails: "Email",
    welcome_email_not_sent: "Δεν στάλθηκε",

    // Review Moderation
    pending_reviews: "Εκκρεμείς Κριτικές",
    pending_reviews_desc: "Αξιολόγηση και συντονισμός σχολίων πελατών",
    approved_reviews: "Εγκεκριμένες Κριτικές",
    loading_reviews_admin: "Φόρτωση κριτικών...",
    no_pending_reviews: "Δεν υπάρχουν εκκρεμείς κριτικές",
    approve: "Έγκριση",
    delete: "Διαγραφή",
    unapprove: "Ακύρωση Έγκρισης",
    pending: "Εκκρεμεί",
    approved: "Εγκρίθηκε",
    review_status_updated: "Η κατάσταση της κριτικής ενημερώθηκε",
    review_deleted: "Η κριτική διαγράφηκε",

    // File Upload
    upload_product_files: "Μεταφόρτωση Αρχείων Προϊόντων",
    upload_product_files_desc: "Μεταφορτώστε αρχεία PDF για αγορασμένα προϊόντα. Τα αρχεία θα συσχετιστούν με tokens λήψης.",
    invalid_file_type: "Μη Έγκυρος Τύπος Αρχείου",
    invalid_file_type_desc: "Παρακαλώ επιλέξτε ένα αρχείο PDF.",
    missing_information: "Ελλιπείς Πληροφορίες",
    missing_information_desc: "Παρακαλώ συμπληρώστε όλα τα απαιτούμενα πεδία.",
    upload_successful: "Επιτυχής Μεταφόρτωση",
    upload_successful_desc: "Το αρχείο μεταφορτώθηκε επιτυχώς για το token: {token}",
    upload_failed: "Αποτυχία Μεταφόρτωσης",
    upload_failed_desc: "Αποτυχία μεταφόρτωσης αρχείου. Παρακαλώ δοκιμάστε ξανά.",
    download_token: "Token Λήψης",
    download_token_desc: "Το μοναδικό token λήψης από την εγγραφή αγοράς",
    product_sku: "SKU Προϊόντος",
    product_sku_desc: "Το SKU του προϊόντος που μεταφορτώνεται",
    pdf_file: "Αρχείο PDF",
    upload_file: "Μεταφόρτωση Αρχείου",
    uploading: "Μεταφόρτωση...",
    upload_info_alert: "Για να μεταφορτώσετε ένα αρχείο, χρειάζεστε το token λήψης από μια αγορά και το SKU προϊόντος. Μπορείτε να τα βρείτε στη Λίστα Αγορών παρακάτω.",
    file_storage_path: "Διαδρομή Αποθήκευσης Αρχείου:",
    file_storage_info: "Τα αρχεία αποθηκεύονται με ασφάλεια και μπορούν να ληφθούν μόνο από χρήστες που κατέχουν την αντίστοιχη αγορά.",

    // Download Analytics
    total_downloads: "Συνολικές Λήψεις",
    total_downloads_desc: "Τελευταίες 100 καταγεγραμμένες λήψεις",
    top_product: "Κορυφαίο Προϊόν",
    downloads_count: "{count} λήψεις",
    most_downloaded_products: "Τα Πιο Δημοφιλή Προϊόντα",
    most_downloaded_products_desc: "Προϊόντα ταξινομημένα κατά συχνότητα λήψης",
    product_sku_label: "SKU Προϊόντος",
    recent_downloads: "Πρόσφατες Λήψεις",
    recent_downloads_desc: "Τελευταίες λήψεις προϊόντων με χρονοσήματα",
    downloaded_at: "Λήφθηκε Στις",
    no_downloads_recorded: "Δεν έχουν καταγραφεί λήψεις ακόμα",
    no_recent_downloads: "Δεν υπάρχουν πρόσφατες λήψεις",

    // Download Limit Manager
    set_custom_download_limit: "Ορισμός Προσαρμοσμένου Ορίου Λήψεων",
    set_custom_download_limit_desc: "Δημιουργήστε προσαρμοσμένα όρια λήψεων για συγκεκριμένους χρήστες ή ρόλους. Τα όρια ειδικά για χρήστη έχουν προτεραιότητα έναντι των ορίων βάσει ρόλου.",
    limit_type: "Τύπος Ορίου",
    user_specific: "Ειδικό για Χρήστη",
    role_based: "Βάσει Ρόλου",
    select_user: "Επιλογή Χρήστη",
    choose_user: "Επιλέξτε έναν χρήστη",
    select_role: "Επιλογή Ρόλου",
    choose_role: "Επιλέξτε έναν ρόλο",
    daily_download_limit: "Ημερήσιο Όριο Λήψεων",
    create_limit: "Δημιουργία Ορίου",
    active_download_limits: "Ενεργά Όρια Λήψεων",
    active_download_limits_desc: "Διαχείριση προσαρμοσμένων ορίων λήψεων. Το προεπιλεγμένο όριο είναι 10 λήψεις ανά ημέρα.",
    type: "Τύπος",
    target: "Στόχος",
    daily_limit: "Ημερήσιο Όριο",
    user: "Χρήστης",
    downloads_per_day: "{limit} λήψεις/ημέρα",
    delete_download_limit: "Διαγραφή Ορίου Λήψεων;",
    delete_download_limit_desc: "Αυτό θα αφαιρέσει το προσαρμοσμένο όριο και θα επαναφέρει το προεπιλεγμένο όριο των 10 λήψεων ανά ημέρα.",
    no_custom_limits: "Δεν έχουν οριστεί προσαρμοσμένα όρια λήψεων. Το προεπιλεγμένο όριο των 10 λήψεων ανά ημέρα ισχύει για όλους τους χρήστες.",
    loading_download_limits: "Φόρτωση ορίων λήψεων...",
    download_limit_created: "Το όριο λήψεων δημιουργήθηκε με επιτυχία",
    download_limit_create_failed: "Αποτυχία δημιουργίας ορίου λήψεων",
    download_limit_deleted: "Το όριο λήψεων διαγράφηκε με επιτυχία",
    download_limit_delete_failed: "Αποτυχία διαγραφής ορίου λήψεων",
    limit_must_be_positive: "Το ημερήσιο όριο πρέπει να είναι θετικός αριθμός",
    please_select_user: "Παρακαλώ επιλέξτε έναν χρήστη",
    please_select_role: "Παρακαλώ επιλέξτε έναν ρόλο",
    unknown_user: "Άγνωστος",

    // Notification Settings
    email_notifications_admin: "Ειδοποιήσεις Email",
    email_notifications_admin_desc: "Ρυθμίστε ποιες ενέργειες διαχειριστή ενεργοποιούν ειδοποιήσεις email",
    loading_preferences: "Φόρτωση προτιμήσεων...",
    receive_emails_at: "Θα λαμβάνετε emails στο: {email}",
    receive_notifications_when: "Λάβετε ειδοποιήσεις όταν εκτελείται αυτή η ενέργεια",
    download_limit_reset_action: "Επαναφορά Ορίου Λήψεων",
    custom_limit_created_action: "Δημιουργήθηκε Προσαρμοσμένο Όριο",
    custom_limit_deleted_action: "Διαγράφηκε Προσαρμοσμένο Όριο",
    notification_preferences_updated: "Οι προτιμήσεις ειδοποιήσεων ενημερώθηκαν",
    notification_preferences_failed: "Αποτυχία ενημέρωσης προτιμήσεων",
    notification_note: "Σημείωση: Οι ειδοποιήσεις αποστέλλονται άμεσα όταν εκτελούνται ενέργειες. Βεβαιωθείτε ότι η διεύθυνση email σας είναι σωστή και ελέγξτε τον φάκελο spam εάν δεν λαμβάνετε ειδοποιήσεις.",

    // Audit Log
    filters: "Φίλτρα",
    action_type: "Τύπος Ενέργειας",
    admin_email: "Email Διαχειριστή",
    start_date: "Ημερομηνία Έναρξης",
    end_date: "Ημερομηνία Λήξης",
    all_actions: "Όλες οι Ενέργειες",
    reset_limit_action: "Επαναφορά Ορίου",
    create_limit_action: "Δημιουργία Ορίου",
    delete_limit_action: "Διαγραφή Ορίου",
    search_by_email: "Αναζήτηση με email...",
    clear_filters: "Εκκαθάριση Φίλτρων",
    admin_audit_log: "Αρχείο Ελέγχου Διαχειριστή",
    admin_audit_log_desc: "Παρακολούθηση όλων των διαχειριστικών ενεργειών συμπεριλαμβανομένων των επαναφορών ορίων λήψεων και των αλλαγών προσαρμοσμένων ορίων",
    entry: "καταχώρηση",
    entries: "καταχωρήσεις",
    export_csv: "Εξαγωγή CSV",
    export_pdf: "Εξαγωγή PDF",
    timestamp: "Χρονοσήμανση",
    admin_label: "Διαχειριστής",
    action: "Ενέργεια",
    details: "Λεπτομέρειες",
    limit_label: "Όριο: {limit} λήψεις/ημέρα",
    no_audit_logs_filters: "Δεν υπάρχουν αρχεία ελέγχου που να ταιριάζουν με τα επιλεγμένα φίλτρα.",
    no_audit_logs_found: "Δεν βρέθηκαν αρχεία ελέγχου. Οι ενέργειες διαχειριστή θα εμφανιστούν εδώ.",
    loading_audit_logs: "Φόρτωση αρχείων ελέγχου...",
    new_admin_action: "Νέα Ενέργεια Διαχειριστή",
    performed_action: "εκτέλεσε: {action}",
    generated_at: "Δημιουργήθηκε: {date}",
    filters_label: "Φίλτρα: {filters}",
    from_date: "Από: {date}",
    to_date: "Έως: {date}",

    // IP Whitelist Manager
    ip_whitelist_management: "Διαχείριση Whitelist IP",
    ip_whitelist_management_desc: "Διαχειριστείτε αξιόπιστες IP και εύρη CIDR που παρακάμπτουν τον περιορισμό ρυθμού για webhooks και API endpoints",
    add_ip_to_whitelist: "Προσθήκη IP στο Whitelist",
    add_whitelisted_ip: "Προσθήκη IP ή Εύρους CIDR στο Whitelist",
    add_whitelisted_ip_desc: "Προσθέστε μια αξιόπιστη διεύθυνση IP ή εύρος CIDR που θα παρακάμψει τον περιορισμό ρυθμού",
    ip_address_or_cidr: "Διεύθυνση IP ή Εύρος CIDR",
    ip_cidr_placeholder: "192.168.1.1 ή 192.168.1.0/24",
    ip_cidr_helper: "Εισάγετε μια μεμονωμένη IP (192.168.1.1) ή εύρος CIDR (192.168.1.0/24)",
    ip_cidr_range_column: "IP / Εύρος CIDR",
    cidr_range: "Εύρος CIDR",
    single_ip: "Μεμονωμένη IP",
    please_enter_ip: "Παρακαλώ εισάγετε μια διεύθυνση IP ή εύρος CIDR",
    invalid_ip_format: "Παρακαλώ εισάγετε μια έγκυρη διεύθυνση IP (π.χ., 192.168.1.1) ή εύρος CIDR (π.χ., 192.168.1.0/24)",
    invalid_cidr_prefix: "Το πρόθεμα CIDR πρέπει να είναι μεταξύ 0 και 32",
    ip_added_to_whitelist: "Η διεύθυνση IP προστέθηκε στο whitelist",
    ip_add_failed: "Αποτυχία προσθήκης διεύθυνσης IP",
    ip_removed_from_whitelist: "Η διεύθυνση IP αφαιρέθηκε από το whitelist",
    ip_remove_failed: "Αποτυχία αφαίρεσης διεύθυνσης IP",
    no_whitelisted_ips: "Δεν υπάρχουν ακόμα IP στο whitelist. Προσθέστε αξιόπιστες IP ή εύρη CIDR για να παρακάμψουν τα όρια ρυθμού.",

    // Pagination
    previous: "Προηγούμενο",
    next: "Επόμενο",

    // Date Range Presets
    date_presets: "Γρήγορη Επιλογή",
    last_7_days: "Τελευταίες 7 ημέρες",
    last_30_days: "Τελευταίες 30 ημέρες",
    this_month: "Αυτός ο μήνας",
    custom_range: "Προσαρμοσμένο",

    // Audit Log Action Types
    approve_review: "Έγκριση Κριτικής",
    unapprove_review: "Ακύρωση Έγκρισης Κριτικής",
    delete_review: "Διαγραφή Κριτικής",
    update_product: "Ενημέρωση Προϊόντος",
    toggle_product_status: "Αλλαγή Κατάστασης Προϊόντος",
    product_activated: "Προϊόν Ενεργοποιήθηκε",
    product_deactivated: "Προϊόν Απενεργοποιήθηκε",
    review_id: "ID Κριτικής",
    review_rating: "Βαθμολογία",
    review_by: "Κριτική από",
    
    // Review Moderation
    all_reviews: "Όλες οι Κριτικές",
    all_reviews_desc: "Οι κριτικές δημοσιεύονται αυτόματα. Μπορείτε να διαγράψετε ακατάλληλες κριτικές.",
    published: "Δημοσιευμένη",
    no_reviews_yet_admin: "Δεν υπάρχουν κριτικές ακόμα.",

    // Common Admin Terms
    na: "Δ/Υ",
    completed: "Ολοκληρώθηκε",
    admin_role: "ΔΙΑΧΕΙΡΙΣΤΗΣ",
    user_role: "ΧΡΗΣΤΗΣ",
    
    // Password Strength
    password_min_length: "Τουλάχιστον 8 χαρακτήρες",
    password_uppercase: "Τουλάχιστον ένα κεφαλαίο γράμμα",
    password_lowercase: "Τουλάχιστον ένα πεζό γράμμα",
    password_number: "Τουλάχιστον έναν αριθμό",
    password_weak: "Αδύναμος",
    password_fair: "Μέτριος",
    password_good: "Καλός",
    password_strong: "Ισχυρός",
    password_compromised: "Παραβιασμένος κωδικός",
    password_compromised_desc: "Αυτός ο κωδικός έχει εμφανιστεί σε διαρροές δεδομένων. Επιλέξτε έναν διαφορετικό.",
    password_checking_breach: "Έλεγχος ασφαλείας κωδικού...",
    password_times_seen: "φορές εντοπισμένος",
    
    // Contact Page
    contact_title: "Επικοινωνία",
    contact_subtitle: "Έχεις απορίες ή χρειάζεσαι βοήθεια; Είμαστε εδώ για εσένα.",
    contact_form_title: "Στείλε μας μήνυμα",
    contact_name: "Όνομα",
    contact_name_placeholder: "Το όνομά σου",
    contact_email_placeholder: "Το email σου",
    contact_message: "Μήνυμα",
    contact_message_placeholder: "Γράψε το μήνυμά σου εδώ...",
    contact_send: "Αποστολή",
    contact_sending: "Αποστολή...",
    contact_success_title: "Ευχαριστούμε!",
    contact_success_desc: "Το μήνυμά σου στάλθηκε. Θα απαντήσουμε εντός 24–48 ωρών.",
    contact_info_title: "Στοιχεία Επικοινωνίας",
    contact_response_time_label: "Χρόνος Απάντησης",
    contact_response_time: "Εντός 24–48 ωρών, Δευτέρα–Παρασκευή",
    contact_useful_links: "Χρήσιμοι Σύνδεσμοι",
    contact_link_faq: "Συχνές Ερωτήσεις",
    contact_link_terms: "Όροι Χρήσης & Επιστροφές",
    contact_link_privacy: "Πολιτική Απορρήτου",
    
    // Need Help Block
    need_help_title: "Χρειάζεσαι βοήθεια;",
    need_help_desc: "Αν έχεις ερωτήσεις για κάποιο πρόγραμμα ή χρειάζεσαι υποστήριξη, μη διστάσεις να επικοινωνήσεις μαζί μας.",
    need_help_cta: "Επικοινώνησε μαζί μας",
    
    // Footer extra
    faq: "Συχνές Ερωτήσεις",
  },
  en: {
    // Navigation
    home: "Home",
    programs: "Programs",
    nutrition: "Nutrition",
    signup: "Sign Up",
    
    // Hero Section
    hero_title: "Build Strength.",
    hero_subtitle: "Transform Your Body.",
    hero_description: "Complete PDF training programs — strength, hypertrophy, mobility — with step-by-step instructions and coach support.",
    hero_cta1: "View Programs",
    hero_cta2: "Book 1:1 Session",
    hero_bullet1: "Structured 6–12 week programs with progressive overload",
    hero_bullet2: "Detailed technique instructions for every exercise",
    hero_bullet3: "Personal support through 1:1 coaching sessions",
    hero_trust1: "Instant digital delivery",
    hero_trust2: "Secure payments",
    hero_trust3: "Coach support included",
    
    // About Section
    about_title: "Hi, I'm Stefania!",
    about_p1: "As a certified strength and nutrition coach, I've helped hundreds of people discover their strength - not just in the gym, but in their lives.",
    about_p2: "My philosophy is simple: strength is built through consistency, support, and the right guidance. It's not about extreme diets or unrealistic goals - it's about sustainable transformation that lasts.",
    about_p3: "Whatever your goal - lose fat, build muscle, get stronger - I'm here to support you every step of the way.",
    
    // Featured Products
    featured_title: "Featured Programs",
    featured_description: "Choose the program that fits your goals and start your transformation today.",
    view_all_programs: "View All Programs",
    
    // How It Works
    how_it_works: "How It Works",
    how_it_works_subtitle: "From choosing a program to your first workout — in 4 simple steps.",
    step1_title: "Choose a program",
    step1_description: "Browse our programs and find the one that matches your level and goals.",
    step2_title: "Complete payment",
    step2_description: "Secure card payment via Stripe. We never store your card details.",
    step3_title: "Get instant access",
    step3_description: "After payment, you'll receive a confirmation email and can download the PDF immediately.",
    step4_title: "Start training + support",
    step4_description: "Follow the program step by step. If you need help, email us or book a 1:1 session.",
    how_it_works_time: "Weekly time commitment: 3–5 workouts × 45–75 min, depending on the program.",
    how_it_works_contact: "Have questions?",
    how_it_works_contact_link: "Send us a message",
    
    // Social Proof
    social_proof_title: "Real Results from Real People",
    social_proof_subtitle: "See what people who followed our programs have to say.",
    social_proof_disclaimer: "Results vary by individual.",
    social_proof_cta: "Find the program that fits me",
    social_proof_early_badge: "New Platform",
    social_proof_early_title: "Be among the first to try my programs",
    social_proof_early_description: "The programs just launched. Start now and share your experience — your feedback matters.",
    social_proof_early_cta: "View Programs",
    
    // CTA
    cta_title: "Ready to Get Started?",
    cta_description: "Don't wait any longer. Your journey to becoming your best self starts today.",
    view_programs: "View Programs",
    
    // Reviews Section
    loading_reviews: "Loading reviews...",
    no_reviews: "No reviews yet.",
    submit_review: "Submit Review",
    your_rating: "Your Rating",
    your_comment: "Your Comment",
    submit: "Submit",
    submitting: "Submitting...",
    review_success: "Success!",
    review_success_desc: "Your review has been published!",
    review_error: "Error",
    review_error_desc: "Something went wrong. Please try again.",
    toast_review_blocked: "Please revise your review. Some words aren't appropriate for our community.",
    toast_review_blocked_spam: "Please write a more natural review without excessive capitals or repeated characters.",
    
    // Footer
    footer_description: "Specialized training and nutrition programs for real transformation.",
    footer_by: "By Stefania Meraklis",
    quick_links: "Quick Links",
    about: "About",
    contact: "Contact",
    all_rights: "All rights reserved.",
    privacy_policy: "Privacy Policy",
    terms_of_service: "Terms of Service",
    
    // Programs Page
    programs_hero_title: "My Programs",
    programs_hero_subtitle: "Professional strength and transformation programs designed for real results.",
    faq1_question: "Who are these programs for?",
    faq1_answer: "The programs are for everyone - from beginners to advanced athletes. Each program includes clear instructions and can be adapted to your level.",
    faq2_question: "Do I need gym equipment?",
    faq2_answer: "Most programs require basic gym equipment (barbell, weights, bench). If you have questions about specific equipment, don't hesitate to ask!",
    faq3_question: "How long does each program last?",
    faq3_answer: "Duration ranges from 4 to 12 weeks depending on the program. Each product has detailed information about duration and content.",
    faq4_question: "Can I get personal guidance?",
    faq4_answer: "Yes! I offer 1:1 online coaching sessions for personalized guidance. Check the available programs or contact me for more information.",
    faq5_question: "What does the cost include?",
    faq5_answer: "The cost includes access to the complete program in PDF format with clear instructions, workout schedules, and all necessary materials to get started immediately.",
    
    // Meal Plans Page
    meal_plans_title: "Meal Plans",
    meal_plans_subtitle: "Customized meal plans will be available soon. Stay tuned!",
    coming_soon: "Coming Soon",
    coming_soon_description: "Customized meal plans will be available soon. Stay tuned!",
    find_ideal_plan: "Find Your Ideal Plan",
    find_ideal_plan_description: "Answer a few questions to find the right nutrition program for you.",
    body_weight: "Body Weight",
    under_60kg: "Under 60kg",
    weight_60_80kg: "60-80kg",
    over_80kg: "Over 80kg",
    activity_level: "Activity Level",
    select_activity: "Select activity level",
    sedentary: "Sedentary (little or no exercise)",
    moderate: "Moderate activity (exercise 3-5 times per week)",
    high: "High activity (exercise 6-7 times per week)",
    recommended_plan: "Recommended Plan",
    recommended_plan_description: "Based on your details, this plan will help you achieve your goals.",
    daily_calories: "Personalized calorie intake",
    recipes_tips: "100+ recipes and cooking tips",
    grocery_lists: "Weekly grocery lists",
    meal_alternatives: "Meal alternatives",
    recommended_price: "Recommended Price",
    buy_now_btn: "Buy Now",
    looking_for_package: "Looking for a Complete Package?",
    transformation_description: "Combine training and nutrition for maximum results with my Transformation Program.",
    weeks_training: "12 weeks of structured training",
    custom_meal_plan: "Custom meal plan",
    monthly_checkins: "Monthly progress check-ins",
    email_support: "Email support",
    learn_more: "Learn More",
    
    // Product Detail
    back_to_programs: "Back to Programs",
    back_to_programs_link: "Back to Programs",
    breadcrumb_home: "Home",
    breadcrumb_programs: "Programs",
    whats_included: "What's Included",
    duration: "Duration",
    duration_label: "Duration",
    level: "Level",
    level_label: "Level",
    format: "Format",
    format_label: "Format",
    equipment_label: "Equipment",
    workout_time_label: "Workout Time",
    days_per_week_label: "Days / Week",
    delivery_label: "Delivery",
    support_label: "Support",
    buy_program_cta: "Buy This Program",
    buy_now_price: "Buy Now",
    secure_payment: "Secure payment via Stripe · Instant digital delivery",
    about_program: "About the Program",
    who_is_this_for: "Who Is This For",
    program_specs: "Program Specs",
    faq_title: "Frequently Asked Questions",
    faq_title_detail: "Questions About This Program",
    refund_note: "Due to digital nature, refunds are not available.",
    refund_link: "See refund policy",
    product_not_found: "Product Not Found",
    anonymous: "Anonymous",
    no_reviews_yet: "No reviews yet",
    leave_review: "Leave a Review",
    sign_in_to_review: "Sign in to leave a review",
    rating: "Rating",
    your_review: "Your Review",
    share_experience: "Share your experience...",
    
    // Header
    my_purchases: "My Purchases",
    admin: "Admin",
    admin_dashboard: "Admin Dashboard",
    settings: "Settings",
    logout: "Logout",
    
    // Auth Pages
    join_morefitlyfe: "Join MoreFitLyfe",
    start_transformation: "Start your transformation journey today",
    sign_up: "Sign Up",
    log_in: "Log In",
    full_name: "Full Name",
    email: "Email",
    password: "Password",
    i_agree_to: "I agree to the",
    and: "and",
    create_account: "Create Account",
    creating_account: "Creating Account...",
    logging_in: "Logging in...",
    back_to_home: "Back to Home",
    loading: "Loading...",
    
    // Forgot/Reset Password
    forgot_password: "Forgot password?",
    forgot_password_desc: "Enter your email and we'll send you a reset link.",
    send_reset_link: "Send reset link",
    sending: "Sending...",
    password_reset_sent: "Email sent",
    password_reset_sent_desc: "Check your inbox for the reset link.",
    check_your_email: "Check your email",
    password_reset_instructions: "We've sent you a password reset link. Check your inbox and spam folder.",
    try_another_email: "Try another email",
    back_to_login: "Back to login",
    reset_password: "Reset password",
    reset_password_desc: "Enter your new password.",
    new_password: "New password",
    confirm_password: "Confirm password",
    update_password: "Update password",
    updating: "Updating...",
    password_updated: "Password updated",
    password_updated_desc: "Your password has been updated successfully.",
    password_reset_success: "Reset successful",
    password_reset_success_desc: "Your password has been reset successfully.",
    invalid_reset_link: "This reset link is invalid or has expired.",
    request_new_link: "Request new link",
    redirecting_home: "You will be redirected to the home page...",
    go_to_home: "Go to home",
    error: "Error",
    
    // Email Verification
    verify_email_title: "Verify Your Email",
    verify_email_sent: "Check your email",
    verify_email_sent_desc: "We've sent you a verification email. Click the link to activate your account.",
    resend_verification: "Resend verification email",
    resending: "Resending...",
    verification_sent: "Email sent",
    verification_sent_desc: "Check your inbox and spam folder.",
    verification_success_title: "Email Verified!",
    verification_success_desc: "Your account is now active. You can sign in.",
    redirecting_to_login: "Redirecting to login",
    go_to_login: "Go to login",
    email_not_verified: "Your email is not verified",
    email_not_verified_desc: "Please check your inbox and click the verification link.",
    
    // Toast Messages
    toast_sign_in_required: "Please sign in to leave a review",
    toast_review_submitted: "Review submitted! It will appear after approval.",
    toast_review_failed: "Failed to submit review",
    toast_settings_saved: "Settings saved",
    toast_settings_saved_desc: "Your preferences have been updated successfully",
    toast_language_changed: "Language changed",
    toast_language_greek: "Ελληνικά ενεργοποιήθηκαν",
    toast_language_english: "English activated",
    toast_navigating_home: "Navigating to home page",
    toast_navigating_programs: "Navigating to programs",
    toast_navigating_meals: "Navigating to meal plans",
    
    // Settings Page
    settings_title: "Settings",
    settings_description: "Manage your account preferences and settings",
    keyboard_shortcuts: "Keyboard Shortcuts",
    keyboard_shortcuts_desc: "Enable or disable keyboard shortcuts for faster navigation",
    keyboard_shortcuts_help: "Press ? to view all available shortcuts",
    language_preferences: "Language Preferences",
    language_preferences_desc: "Choose your preferred language for the interface",
    default_language: "Default Language",
    greek: "Ελληνικά",
    english: "English",
    change: "Change",
    notification_preferences: "Notification Preferences",
    notification_preferences_desc: "Manage how and when you want to receive notifications",
    email_notifications: "Email Notifications",
    email_notifications_desc: "Receive notifications via email",
    push_notifications: "Push Notifications",
    push_notifications_desc: "Receive push notifications on your device",
    marketing_emails: "Marketing Emails",
    marketing_emails_desc: "Receive special offers and product updates",
    system_updates: "System Updates",
    system_updates_desc: "Important notifications about your account",
    audit_sounds: "Admin Audit Sounds",
    audit_sounds_desc: "Play sound for critical admin actions (deletions, resets)",
    preview_sounds: "Preview sounds",
    sound_default: "Default",
    sound_warning: "Warning",
    sound_destructive: "Destructive",
    save_settings: "Save Settings",
    
    // Two-Factor Authentication
    "2fa_title": "Two-Factor Authentication",
    "2fa_description": "Add an extra layer of security to your account using an authenticator app.",
    "2fa_enabled": "Enabled",
    "2fa_disabled": "Disabled",
    "2fa_enable": "Enable 2FA",
    "2fa_disable": "Disable 2FA",
    "2fa_scan_qr": "Scan this QR code with your authenticator app (e.g., Google Authenticator, Authy):",
    "2fa_manual_entry": "Or enter this code manually:",
    "2fa_enter_code": "Enter the 6-digit code",
    "2fa_verify": "Verify",
    "2fa_verify_enable": "Verify & Enable",
    "2fa_verifying": "Verifying...",
    "2fa_setting_up": "Setting up...",
    "2fa_disabling": "Disabling...",
    "2fa_active_message": "Two-factor authentication is active on your account.",
    "2fa_not_enabled_message": "Protect your account with two-factor authentication. You'll need an authenticator app on your phone.",
    "2fa_disable_confirm_title": "Disable 2FA?",
    "2fa_disable_confirm_desc": "This will remove the extra security layer from your account. You'll need to set it up again if you want to enable it later.",
    "2fa_verification_title": "Verify Your Identity",
    "2fa_verification_description": "Enter the code from your authenticator app to continue.",
    "2fa_secret_copied": "Copied!",
    "2fa_secret_copied_desc": "Secret key copied to clipboard.",
    
    // Keyboard Shortcuts Modal
    shortcuts_title: "Keyboard Shortcuts",
    shortcuts_description: "Use these shortcuts for quick navigation",
    shortcut_home: "Go to home",
    shortcut_programs: "Go to programs",
    shortcut_meals: "Go to meal plans",
    shortcut_toggle_language: "Toggle language",
    shortcut_show_shortcuts: "Show shortcuts",

    // Admin Dashboard
    admin_dashboard_title: "Admin Dashboard",
    admin_dashboard_subtitle: "Manage your business operations and track performance",
    admin_overview: "Overview",
    admin_products: "Products",
    admin_purchases: "Purchases",
    admin_files: "Files",
    admin_reviews: "Reviews",
    admin_users: "Users",
    admin_revenue: "Revenue",
    admin_downloads: "Downloads",
    admin_limits: "Limits",
    admin_audit: "Audit",
    total_revenue: "Total Revenue",
    total_purchases: "Total Purchases",
    pending_reviews_count: "Pending Reviews",
    total_users: "Total Users",
    view_in_revenue_tab: "View in Revenue tab",
    view_in_purchases_tab: "View in Purchases tab",
    view_in_reviews_tab: "View in Reviews tab",
    view_in_users_tab: "View in Users tab",
    analytics_overview: "Analytics Overview",
    analytics_overview_desc: "View detailed analytics in the tabs above",

    // Product Management
    product_management: "Product Management",
    product_management_desc: "Manage your training programs and products",
    loading_products: "Loading products...",
    edit_product: "Edit Product",
    edit_product_desc: "Update product details and pricing",
    name_english: "Name (English)",
    name_greek: "Name (Greek)",
    description_english: "Description (English)",
    description_greek: "Description (Greek)",
    price_usd: "Price ($)",
    image_url: "Image URL",
    cancel: "Cancel",
    save_changes: "Save Changes",
    saving: "Saving...",
    active: "Active",
    product_updated: "Product updated successfully",
    product_update_failed: "Failed to update product",
    product_status_updated: "Product status updated",

    // Purchase List
    purchase_history: "Purchase History",
    purchase_history_desc: "View all customer purchases and transaction details",
    loading_purchases: "Loading purchases...",
    no_purchases_yet: "No purchases yet",
    date: "Date",
    customer: "Customer",
    product: "Product",
    amount: "Amount",
    status: "Status",

    // User Management
    user_management: "User Management",
    user_management_desc: "View and manage registered users",
    loading_users: "Loading users...",
    no_users_yet: "No users yet",
    name: "Name",
    role: "Role",
    downloads: "Downloads",
    last_download: "Last Download",
    registered: "Registered",
    actions: "Actions",
    never: "Never",
    reset_download_limit: "Reset Download Limit",
    reset_limit_confirm_title: "Reset Download Limit?",
    reset_limit_confirm_desc: "This will clear all download history for {user} and allow them to download files again. This action cannot be undone.",
    reset_limit: "Reset Limit",
    download_limit_reset_success: "Download limit reset successfully",
    download_limit_reset_failed: "Failed to reset download limit",
    resend_welcome_email: "Resend Welcome Email",
    resend_welcome_email_confirm_title: "Resend Welcome Email?",
    resend_welcome_email_confirm_desc: "This will send a new welcome email to {user}.",
    resend_welcome_email_success: "Welcome email sent successfully",
    
    // Email Template Editor
    email_template_editor: "Email Template Editor",
    email_template_editor_desc: "Customize the content and appearance of welcome emails",
    email_template_load_error: "Failed to load email template",
    email_template_saved: "Email template saved successfully",
    email_template_save_error: "Failed to save email template",
    email_template_reset: "Template reset to defaults",
    email_template_not_found: "Email template not found",
    reset_to_default: "Reset to Default",
    save_template: "Save Template",
    content: "Content",
    branding: "Branding",
    preview: "Preview",
    email_subject: "Email Subject",
    email_heading: "Heading",
    greeting_prefix: "Greeting Prefix",
    intro_text: "Introduction Text",
    body_text: "Body Text",
    cta_button_text: "Button Text",
    features_heading: "Features Heading",
    feature_list: "Feature List",
    feature_1_placeholder: "Feature 1",
    feature_2_placeholder: "Feature 2",
    feature_3_placeholder: "Feature 3",
    footer_text: "Footer Text",
    signature: "Signature",
    primary_color: "Primary Color",
    primary_color_desc: "Color for buttons and headings",
    background_color: "Background Color",
    background_color_desc: "Email background color",
    text_color: "Text Color",
    text_color_desc: "Main text color",
    admin_emails: "Emails",
    resend_welcome_email_failed: "Failed to send welcome email",
    welcome_email_sent: "Sent",
    welcome_email_not_sent: "Not Sent",

    // Review Moderation
    pending_reviews: "Pending Reviews",
    pending_reviews_desc: "Review and moderate customer feedback",
    approved_reviews: "Approved Reviews",
    loading_reviews_admin: "Loading reviews...",
    no_pending_reviews: "No pending reviews",
    approve: "Approve",
    delete: "Delete",
    unapprove: "Unapprove",
    pending: "Pending",
    approved: "Approved",
    review_status_updated: "Review status updated",
    review_deleted: "Review deleted",

    // File Upload
    upload_product_files: "Upload Product Files",
    upload_product_files_desc: "Upload PDF files for purchased products. Files will be associated with download tokens.",
    invalid_file_type: "Invalid File Type",
    invalid_file_type_desc: "Please select a PDF file.",
    missing_information: "Missing Information",
    missing_information_desc: "Please provide all required fields.",
    upload_successful: "Upload Successful",
    upload_successful_desc: "File uploaded successfully for token: {token}",
    upload_failed: "Upload Failed",
    upload_failed_desc: "Failed to upload file. Please try again.",
    download_token: "Download Token",
    download_token_desc: "The unique download token from the purchase record",
    product_sku: "Product SKU",
    product_sku_desc: "The SKU of the product being uploaded",
    pdf_file: "PDF File",
    upload_file: "Upload File",
    uploading: "Uploading...",
    upload_info_alert: "To upload a file, you need the download token from a purchase and the product SKU. You can find these in the Purchase List below.",
    file_storage_path: "File Storage Path:",
    file_storage_info: "Files are stored securely and can only be downloaded by users who own the corresponding purchase.",

    // Download Analytics
    total_downloads: "Total Downloads",
    total_downloads_desc: "Last 100 recorded downloads",
    top_product: "Top Product",
    downloads_count: "{count} downloads",
    most_downloaded_products: "Most Downloaded Products",
    most_downloaded_products_desc: "Products ranked by download frequency",
    product_sku_label: "Product SKU",
    recent_downloads: "Recent Downloads",
    recent_downloads_desc: "Latest product downloads with timestamps",
    downloaded_at: "Downloaded At",
    no_downloads_recorded: "No downloads recorded yet",
    no_recent_downloads: "No recent downloads",

    // Download Limit Manager
    set_custom_download_limit: "Set Custom Download Limit",
    set_custom_download_limit_desc: "Create custom download limits for specific users or roles. User-specific limits take priority over role-based limits.",
    limit_type: "Limit Type",
    user_specific: "User-Specific",
    role_based: "Role-Based",
    select_user: "Select User",
    choose_user: "Choose a user",
    select_role: "Select Role",
    choose_role: "Choose a role",
    daily_download_limit: "Daily Download Limit",
    create_limit: "Create Limit",
    active_download_limits: "Active Download Limits",
    active_download_limits_desc: "Manage custom download limits. Default limit is 10 downloads per day.",
    type: "Type",
    target: "Target",
    daily_limit: "Daily Limit",
    user: "User",
    downloads_per_day: "{limit} downloads/day",
    delete_download_limit: "Delete Download Limit?",
    delete_download_limit_desc: "This will remove the custom limit and revert to the default limit of 10 downloads per day.",
    no_custom_limits: "No custom download limits set. Default limit of 10 downloads per day applies to all users.",
    loading_download_limits: "Loading download limits...",
    download_limit_created: "Download limit created successfully",
    download_limit_create_failed: "Failed to create download limit",
    download_limit_deleted: "Download limit deleted successfully",
    download_limit_delete_failed: "Failed to delete download limit",
    limit_must_be_positive: "Daily limit must be a positive number",
    please_select_user: "Please select a user",
    please_select_role: "Please select a role",
    unknown_user: "Unknown",

    // Notification Settings
    email_notifications_admin: "Email Notifications",
    email_notifications_admin_desc: "Configure which admin actions trigger email notifications",
    loading_preferences: "Loading preferences...",
    receive_emails_at: "You'll receive emails at: {email}",
    receive_notifications_when: "Receive notifications when this action is performed",
    download_limit_reset_action: "Download Limit Reset",
    custom_limit_created_action: "Custom Limit Created",
    custom_limit_deleted_action: "Custom Limit Deleted",
    notification_preferences_updated: "Notification preferences updated",
    notification_preferences_failed: "Failed to update preferences",
    notification_note: "Note: Notifications are sent immediately when actions are performed. Make sure your email address is correct and check your spam folder if you don't receive notifications.",

    // Audit Log
    filters: "Filters",
    action_type: "Action Type",
    admin_email: "Admin Email",
    start_date: "Start Date",
    end_date: "End Date",
    all_actions: "All Actions",
    reset_limit_action: "Reset Limit",
    create_limit_action: "Create Limit",
    delete_limit_action: "Delete Limit",
    search_by_email: "Search by email...",
    clear_filters: "Clear Filters",
    admin_audit_log: "Admin Audit Log",
    admin_audit_log_desc: "Track all administrative actions including download limit resets and custom limit changes",
    entry: "entry",
    entries: "entries",
    export_csv: "Export CSV",
    export_pdf: "Export PDF",
    timestamp: "Timestamp",
    admin_label: "Admin",
    action: "Action",
    details: "Details",
    limit_label: "Limit: {limit} downloads/day",
    no_audit_logs_filters: "No audit logs match the selected filters.",
    no_audit_logs_found: "No audit logs found. Admin actions will appear here.",
    loading_audit_logs: "Loading audit logs...",
    new_admin_action: "New Admin Action",
    performed_action: "performed: {action}",
    generated_at: "Generated: {date}",
    filters_label: "Filters: {filters}",
    from_date: "From: {date}",
    to_date: "To: {date}",

    // IP Whitelist Manager
    ip_whitelist_management: "IP Whitelist Management",
    ip_whitelist_management_desc: "Manage trusted IPs and CIDR ranges that bypass rate limiting for webhooks and API endpoints",
    add_ip_to_whitelist: "Add IP to Whitelist",
    add_whitelisted_ip: "Add Whitelisted IP or CIDR Range",
    add_whitelisted_ip_desc: "Add a trusted IP address or CIDR range that will bypass rate limiting",
    ip_address_or_cidr: "IP Address or CIDR Range",
    ip_cidr_placeholder: "192.168.1.1 or 192.168.1.0/24",
    ip_cidr_helper: "Enter a single IP (192.168.1.1) or CIDR range (192.168.1.0/24)",
    ip_cidr_range_column: "IP / CIDR Range",
    cidr_range: "CIDR Range",
    single_ip: "Single IP",
    please_enter_ip: "Please enter an IP address or CIDR range",
    invalid_ip_format: "Please enter a valid IP address (e.g., 192.168.1.1) or CIDR range (e.g., 192.168.1.0/24)",
    invalid_cidr_prefix: "CIDR prefix must be between 0 and 32",
    ip_added_to_whitelist: "IP address added to whitelist",
    ip_add_failed: "Failed to add IP address",
    ip_removed_from_whitelist: "IP address removed from whitelist",
    ip_remove_failed: "Failed to remove IP address",
    no_whitelisted_ips: "No whitelisted IPs yet. Add trusted IPs or CIDR ranges to bypass rate limits.",

    // Pagination
    previous: "Previous",
    next: "Next",

    // Date Range Presets
    date_presets: "Quick Select",
    last_7_days: "Last 7 days",
    last_30_days: "Last 30 days",
    this_month: "This month",
    custom_range: "Custom",

    // Audit Log Action Types
    approve_review: "Approve Review",
    unapprove_review: "Unapprove Review",
    delete_review: "Delete Review",
    update_product: "Update Product",
    toggle_product_status: "Toggle Product Status",
    product_activated: "Product Activated",
    product_deactivated: "Product Deactivated",
    review_id: "Review ID",
    review_rating: "Rating",
    review_by: "Review by",
    
    // Review Moderation
    all_reviews: "All Reviews",
    all_reviews_desc: "Reviews are published automatically. You can delete inappropriate reviews.",
    published: "Published",
    no_reviews_yet_admin: "No reviews yet.",

    // Common Admin Terms
    na: "N/A",
    completed: "Completed",
    admin_role: "ADMIN",
    user_role: "USER",
    
    // Password Strength
    password_min_length: "At least 8 characters",
    password_uppercase: "At least one uppercase letter",
    password_lowercase: "At least one lowercase letter",
    password_number: "At least one number",
    password_weak: "Weak",
    password_fair: "Fair",
    password_good: "Good",
    password_strong: "Strong",
    password_compromised: "Compromised password",
    password_compromised_desc: "This password has appeared in data breaches. Please choose a different one.",
    password_checking_breach: "Checking password security...",
    password_times_seen: "times seen",
    
    // Contact Page
    contact_title: "Contact",
    contact_subtitle: "Have questions or need help? We're here for you.",
    contact_form_title: "Send us a message",
    contact_name: "Name",
    contact_name_placeholder: "Your name",
    contact_email_placeholder: "Your email",
    contact_message: "Message",
    contact_message_placeholder: "Write your message here...",
    contact_send: "Send",
    contact_sending: "Sending...",
    contact_success_title: "Thank you!",
    contact_success_desc: "Your message was sent. We'll reply within 24–48 hours.",
    contact_info_title: "Contact Information",
    contact_response_time_label: "Response Time",
    contact_response_time: "Within 24–48 hours, Monday–Friday",
    contact_useful_links: "Useful Links",
    contact_link_faq: "Frequently Asked Questions",
    contact_link_terms: "Terms of Service & Refunds",
    contact_link_privacy: "Privacy Policy",
    
    // Need Help Block
    need_help_title: "Need help?",
    need_help_desc: "If you have questions about a program or need support, don't hesitate to contact us.",
    need_help_cta: "Contact us",
    
    // Footer extra
    faq: "FAQ",
  },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem("morefitlyfe-language");
    return (saved === "el" || saved === "en") ? saved : "el";
  });

  useEffect(() => {
    localStorage.setItem("morefitlyfe-language", language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === "el" ? "en" : "el"));
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.el] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      <div key={language} className="animate-fade-in">
        {children}
      </div>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
